import type { StringTable } from '../index.ts';

/**
 * Gameplay screen strings (namespace "game"). EN and AF must have identical
 * keys and placeholders (test/i18n.test.ts). Tone: warm and encouraging —
 * a wrong answer is a learning moment, never a telling-off.
 */
export const GAME: StringTable = {
  en: {
    screenLabel: 'Dropstack game',

    // HUD
    score: 'Score',
    scoreLabel: 'Score: {score}',
    levelTheme: 'Level {level}: {theme}',
    levelShort: 'Lv {level}',
    accuracyLabel: 'Accuracy: {percent} correct',
    accuracyNone: 'Accuracy: no answers yet',
    next: 'Next',
    nextLabel: 'Next sum: {sum}',
    pauseLabel: 'Pause the game',

    // Pause
    pausedTitle: 'Paused',
    pausedText: 'Take a breather! The sum stays hidden until you carry on.',
    resume: 'Keep playing',
    settings: 'Settings',
    quit: 'End this run',
    quitTitle: 'End this run?',
    quitText: 'Your score so far still counts.',
    quitYes: 'Yes, end it',

    // Answer panel
    choicesLabel: 'Answer choices',
    choiceLabel: 'Answer {letter}: {value}',
    keysHint: 'Press 1 to 4 or A to D to answer. Press Escape to pause.',
    modeLabel: 'Answer by',
    modeChoices: 'Choices',
    modeWrite: 'Write',
    modeToWrite: 'Switch to writing your answers',
    modeToChoices: 'Switch to answer choices',

    // Playfield
    wellLabel: 'The well: {bricks} of {max} brick layers',
    stackMeterLabel: 'Stack: {bricks} of {max} layers',
    cleanFloor: 'Clean floor!',
    wrongTitle: 'Not quite!',
    timeoutTitle: "Time's up!",
    answerIs: 'The answer is',
    encourage: "You've got the next one!",
    levelTitle: 'Level {level}',
    levelUnlocked: '{theme} unlocked!',
    levelBack: "Let's practise at {theme} for a bit.",
    levelStepBack: "Let's step back a level",
    levelStepBackHint: "Practise at {theme} for a bit – you'll climb back up in no time!",
    gameOverTitle: 'The stack reached the top!',
    gameOverText: 'Great effort! Here comes your score…',

    // Screen reader announcements
    srProblem: 'New sum. {sum}',
    srCorrect: 'Correct! Plus {points} points.',
    srWrong: 'Not quite. The answer is {answer}.',
    srTimeout: "Time's up. The answer is {answer}.",
    srLevelUp: 'Level {level}: {theme} unlocked!',
    srLevelDown: "Level {level}: {theme}. Let's practise here for a bit.",
    srLevelStepBack: "Let's step back a level. Level {level}: {theme}. You'll climb back up in no time!",
    srGameOver: 'The stack reached the top! The run is over.',
  },
  af: {
    screenLabel: 'Dropstack-speletjie',

    score: 'Telling',
    scoreLabel: 'Telling: {score}',
    levelTheme: 'Vlak {level}: {theme}',
    levelShort: 'Vlak {level}',
    accuracyLabel: 'Akkuraatheid: {percent} reg',
    accuracyNone: 'Akkuraatheid: nog geen antwoorde nie',
    next: 'Volgende',
    nextLabel: 'Volgende som: {sum}',
    pauseLabel: 'Pouseer die speletjie',

    pausedTitle: 'Pouse',
    pausedText: 'Vat ’n blaaskans! Die som bly weggesteek totdat jy verder speel.',
    resume: 'Speel verder',
    settings: 'Instellings',
    quit: 'Beëindig die rondte',
    quitTitle: 'Beëindig hierdie rondte?',
    quitText: 'Jou telling tot dusver tel steeds.',
    quitYes: 'Ja, beëindig dit',

    choicesLabel: 'Antwoordkeuses',
    choiceLabel: 'Antwoord {letter}: {value}',
    keysHint: 'Druk 1 tot 4 of A tot D om te antwoord. Druk Escape om te pouseer.',
    modeLabel: 'Antwoord deur',
    modeChoices: 'Keuses',
    modeWrite: 'Skryf',
    modeToWrite: 'Skakel oor na skryf',
    modeToChoices: 'Skakel oor na antwoordkeuses',

    wellLabel: 'Die put: {bricks} van {max} baksteenlae',
    stackMeterLabel: 'Stapel: {bricks} van {max} lae',
    cleanFloor: 'Skoon vloer!',
    wrongTitle: 'Amper!',
    timeoutTitle: 'Die tyd is om!',
    answerIs: 'Die antwoord is',
    encourage: 'Die volgende een is joune!',
    levelTitle: 'Vlak {level}',
    levelUnlocked: '{theme} oopgesluit!',
    levelBack: 'Kom ons oefen ’n rukkie by {theme}.',
    levelStepBack: 'Kom ons gaan een vlak terug',
    levelStepBackHint: 'Oefen ’n rukkie by {theme} – jy klim sommer gou weer op!',
    gameOverTitle: 'Die stapel het tot bo gegroei!',
    gameOverText: 'Mooi gespeel! Hier kom jou telling…',

    srProblem: 'Nuwe som. {sum}',
    srCorrect: 'Reg so! Plus {points} punte.',
    srWrong: 'Amper. Die antwoord is {answer}.',
    srTimeout: 'Die tyd is om. Die antwoord is {answer}.',
    srLevelUp: 'Vlak {level}: {theme} oopgesluit!',
    srLevelDown: 'Vlak {level}: {theme}. Kom ons oefen ’n rukkie hier.',
    srLevelStepBack: 'Kom ons gaan een vlak terug. Vlak {level}: {theme}. Jy klim sommer gou weer op!',
    srGameOver: 'Die stapel het tot bo gegroei! Die rondte is verby.',
  },
};
